import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Spinner from '../../components/spinner/Spinner.component';
import { GameContext } from '../../core/context/game.context';
import { ChallengeQuestion, PlayQuestionAnswer } from '../../core/interfaces/challenge.interface';
import { Loquis } from '../../core/interfaces/loquis.interface';
import {
  getNextQuestion,
  sendQuestionAnswer,
  sendQuestionTimeout,
  startChallengePlay,
} from '../../core/rest/challenge.service';
import { getLoquis } from '../../core/rest/play.service';
import Game from './Game.component';
import { GameData, LoquisGameData, Question } from './game.interface';

export default function GameService() {
  const gameCtx = React.useContext(GameContext);
  const location = useLocation();
  const [initialLoadingState, setInitialLoadingState] = React.useState(false);
  const [dataState, setDataState] = React.useState<GameData | undefined>(undefined);
  const initRequestIdRef = useRef(0);
  const routePresetId = getRoutePresetId(location.pathname);
  const presetId = gameCtx.getPreset()?.id?.toString();
  const isRouteReady = presetId != null && presetId === routePresetId;

  useEffect(() => {
    if (isRouteReady) {
      setDataState(undefined);
      init(true);
    }
    // init reads the latest context through gameCtx getters; adding it here would restart the play on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRouteReady, presetId, routePresetId]);

  async function init(startNewPlay?: boolean) {
    const requestId = ++initRequestIdRef.current;
    const preset = gameCtx.getPreset();
    const expectedPresetId = preset?.id?.toString();

    if (!expectedPresetId || expectedPresetId !== routePresetId) {
      return;
    }

    const shouldShowSpinner = !dataState;

    if (shouldShowSpinner) {
      setInitialLoadingState(true);
    }

    try {
      let playId = gameCtx.getPlayId();

      if (startNewPlay || !playId) {
        const play = await startChallengePlay(expectedPresetId);

        if (
          !isCurrentInitRequest(
            requestId,
            initRequestIdRef.current,
            expectedPresetId,
            routePresetId,
            gameCtx.getPreset()?.id?.toString()
          )
        ) {
          return;
        }

        playId = play.id;
        gameCtx.setPlayId(playId);
        gameCtx.setScore(play.number_correct_answers);
      }

      const playQuestion = await getNextQuestion(playId);

      if (
        !isCurrentInitRequest(
          requestId,
          initRequestIdRef.current,
          expectedPresetId,
          routePresetId,
          gameCtx.getPreset()?.id?.toString()
        ) ||
        playQuestion.play.challenge_id !== expectedPresetId
      ) {
        return;
      }

      const gameData = await mapPlayQuestionToGameData(playQuestion);

      if (
        !isCurrentInitRequest(
          requestId,
          initRequestIdRef.current,
          expectedPresetId,
          routePresetId,
          gameCtx.getPreset()?.id?.toString()
        )
      ) {
        return;
      }

      setDataState(gameData);
      gameCtx.setScore(gameData.play.number_correct_answers);
    } catch (e: any) {
      console.error(e);
    } finally {
      if (shouldShowSpinner) {
        setInitialLoadingState(false);
      }
    }
  }

  async function answerQuestion(answerId: string): Promise<PlayQuestionAnswer & { loquis: LoquisGameData }> {
    const answer = await sendQuestionAnswer(gameCtx.getPlayId(), answerId);
    const mappedAnswer = await mapPlayAnswer(answer);
    gameCtx.setScore(mappedAnswer.play.number_correct_answers);
    return mappedAnswer;
  }

  async function timeoutQuestion(): Promise<PlayQuestionAnswer & { loquis: LoquisGameData }> {
    const answer = await sendQuestionTimeout(gameCtx.getPlayId());
    const mappedAnswer = await mapPlayAnswer({ ...answer, is_correct: false });
    gameCtx.setScore(mappedAnswer.play.number_correct_answers);
    return mappedAnswer;
  }

  return (
    <section className="GameServiceComponent">
      {initialLoadingState ? <Spinner primaryColor="#7DDC55" secondaryColor="#FE6E5C" /> : null}
      {isRouteReady && !!gameCtx.getPreset() && (
        <Game
          data={dataState}
          onAnswerClick={answerQuestion}
          onQuestionTimeout={timeoutQuestion}
          onNewRoundClick={() => init(false)}
        />
      )}
    </section>
  );
}

function getRoutePresetId(pathname: string) {
  const [, , , , presetId] = pathname.split('/');
  return presetId;
}

function isCurrentInitRequest(
  requestId: number,
  currentRequestId: number,
  expectedPresetId: string,
  routePresetId: string,
  currentPresetId?: string
) {
  return requestId === currentRequestId && expectedPresetId === routePresetId && currentPresetId === expectedPresetId;
}

async function mapPlayAnswer(answer: PlayQuestionAnswer): Promise<PlayQuestionAnswer & { loquis: LoquisGameData }> {
  const loquis = await getLoquis(answer.loquis_id);

  return {
    ...answer,
    loquis: mapLoquis(loquis),
  };
}

async function mapPlayQuestionToGameData(playQuestion: {
  play: GameData['play'];
  question: ChallengeQuestion;
}): Promise<GameData> {
  return {
    play: playQuestion.play,
    question: mapQuestion(playQuestion.question),
    loquis: await getLoquisGameData(playQuestion.question.loquis_id),
  };
}

function mapQuestion(question: ChallengeQuestion): Question {
  return {
    id: question.id,
    text: question.text || question.challenge,
    optionsList: (question.answers || []).map((answer) => ({
      id: answer.id,
      text: answer.text,
    })),
    level: question.position,
  };
}

function mapLoquis(loquis: Loquis): LoquisGameData {
  return {
    audio: loquis.audio,
    picture: {
      url_360: loquis.picture_360,
      url_720: loquis.picture_720,
      title: loquis.name,
    },
    channel: loquis.channel_name,
    localeId: loquis.locale_id,
    id: loquis.id,
  };
}

async function getLoquisGameData(loquisId?: number): Promise<LoquisGameData | undefined> {
  if (!loquisId) return undefined;

  try {
    const loquis = await getLoquis(loquisId);
    return mapLoquis(loquis);
  } catch (e) {
    console.error(e);
    return undefined;
  }
}
